import {skipToken} from '@reduxjs/toolkit/query';
import {Option} from 'platform/components';

import {includes, isNotNil, prop, uniqBy} from 'ramda';

import {useGetTagsQuery} from '@dms/api/core';
import {
  useGetMakeModelWithMakeQuery,
  useGetMakeModelWithMakesQuery,
  useGetSalePresetSettingsListQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useBranches, useVehicleCatalogue} from '@dms/shared';

import {isNotNilOrEmpty, Nullish} from 'shared/web';

type ModelOptionsGroup = {
  label: string;
  options: readonly Option[];
};

export function useVehicleSelectionFilterOptions(
  vehicleType: string | Nullish,
  selectedMakes: string[] | Nullish
) {
  const hasVehicleType = isNotNilOrEmpty(vehicleType);
  const hasMakes = isNotNilOrEmpty(selectedMakes);

  const [vehicleCatalogue, {isLoading: isCatalogueLoading}] = useVehicleCatalogue(vehicleType);

  const {branchOptions, isLoading: isBranchesLoading} = useBranches();

  const {data: makesResponse, isLoading: isMakesLoading} = useGetMakeModelWithMakeQuery(
    hasVehicleType ? {vehicleType, lang: [i18n.language]} : skipToken
  );

  const {data: makesWithModelsResponse, isLoading: isModelsLoading} = useGetMakeModelWithMakesQuery(
    {
      vehicleType: vehicleType ?? '',
      makes: selectedMakes ?? [],
      lang: [i18n.language],
    },
    {skip: !hasVehicleType || !hasMakes}
  );

  const {data: salePresetList, isLoading: isSalePresetsLoading} =
    useGetSalePresetSettingsListQuery();

  const {data: tags, isLoading: isTagsLoading} = useGetTagsQuery({
    resourceId: EntityResourceIds.vehicle,
  });

  const vehicleTypeOptions: Option[] =
    vehicleCatalogue?.vehicleTypeOptions?.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })) ?? [];
  const vehicleConditionOptions: Option[] =
    vehicleCatalogue?.vehicleConditionOptions?.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })) ?? [];
  const salePresetOptions: Option[] =
    salePresetList?.map((preset) => ({label: preset.name, value: preset.id})) ?? [];

  const makeOptions =
    makesResponse?.map((make) => ({
      label: make.default_label,
      value: make.make,
    })) ?? [];

  const modelOptionsGroupedByMake: ModelOptionsGroup[] =
    makesWithModelsResponse
      ?.map((make) => ({
        label: make.default_label,
        options: (make.models ?? []).map((model) => ({
          label: model.default_label,
          value: model.model,
        })),
      }))
      .filter((group) => group.options.length > 0) ?? [];

  const modelFamilyOptionsGroupedByMake: ModelOptionsGroup[] =
    makesWithModelsResponse
      ?.map((make) => ({
        label: make.default_label,
        options: uniqBy(
          prop('group'),
          (make.models ?? []).map((model) => model.model_group).filter(isNotNil)
        ).map((group) => ({
          label: group.default_label,
          value: group.group,
        })),
      }))
      .filter((group) => group.options.length > 0) ?? [];

  const fuelTypeOptions =
    vehicleCatalogue?.fuelTypeOptions?.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })) ?? [];

  const bodyStyleOptions =
    vehicleCatalogue?.vehicleStyleOptions?.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })) ?? [];

  const tagOptions: Option[] =
    tags
      ?.find((tag) => includes(EntityResourceIds.vehicle, tag.allowedResources))
      ?.values.map((value) => ({label: value.name, value: value.id})) ?? [];

  const isMissingVehicleType = !hasVehicleType;
  const isMissingMakes = !hasVehicleType || !hasMakes;

  return {
    vehicleTypeOptions,
    vehicleConditionOptions,
    branchOptions,
    salePresetOptions,
    makeOptions,
    modelFamilyOptionsGroupedByMake,
    modelOptionsGroupedByMake,
    fuelTypeOptions,
    bodyStyleOptions,
    tagOptions,
    isCatalogueLoading,
    isMakeModelLoading: isMakesLoading || isModelsLoading,
    isBranchesLoading,
    isSalePresetsLoading,
    isTagsLoading,
    isMissingVehicleType,
    isMissingMakes,
  };
}
